import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Users, Calendar, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const empEmpty = { name: "", designation: "", department: "Front Office", phone: "", email: "", salary: 0, join_date: "" };

export default function HR() {
    const { activeHotelId } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empEmpty);
    const today = new Date().toISOString().slice(0, 10);

    const load = async () => {
        if (!activeHotelId) return;
        try {
            const [e, a, l] = await Promise.all([
                api.get(`/hr/employees?hotel_id=${activeHotelId}`),
                api.get(`/hr/attendance?hotel_id=${activeHotelId}&date=${today}`),
                api.get(`/hr/leaves?hotel_id=${activeHotelId}`),
            ]);
            setEmployees(e.data); setAttendance(a.data); setLeaves(l.data);
        } catch (er) { toast.error(formatApiError(er)); }
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const submit = async () => {
        try {
            await api.post("/hr/employees", { ...form, hotel_id: activeHotelId, salary: Number(form.salary) });
            toast.success("Employee added");
            setOpen(false); setForm(empEmpty); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const markAtt = async (empId, status) => {
        try {
            await api.post("/hr/attendance", { hotel_id: activeHotelId, employee_id: empId, date: today, status });
            toast.success("Marked");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const attMap = Object.fromEntries(attendance.map(a => [a.employee_id, a.status]));

    return (
        <AppShell title="HR & Payroll" subtitle={`${employees.length} employees`}>
            <Tabs defaultValue="employees">
                <TabsList>
                    <TabsTrigger value="employees" data-testid="tab-emp">Employees</TabsTrigger>
                    <TabsTrigger value="attendance" data-testid="tab-att">Attendance ({today})</TabsTrigger>
                    <TabsTrigger value="leaves" data-testid="tab-leaves">Leaves</TabsTrigger>
                </TabsList>

                <TabsContent value="employees" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="emp-add-btn"><Plus className="w-4 h-4 mr-1" /> Employee</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
                                <div className="grid grid-cols-2 gap-3">
                                    {[["name", "Name"], ["designation", "Designation"], ["department", "Department"], ["phone", "Phone"], ["email", "Email"], ["salary", "Salary", "number"], ["join_date", "Join Date", "date"]].map(([k, l, t]) => (
                                        <div key={k} className={k === "name" ? "col-span-2" : ""}>
                                            <Label className="text-xs uppercase tracking-wider">{l}</Label>
                                            <Input type={t || "text"} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`emp-${k}`} />
                                        </div>
                                    ))}
                                </div>
                                <DialogFooter><Button onClick={submit} disabled={!form.name} className="bg-[#0A1C3E]" data-testid="emp-submit">Create</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Designation</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Salary</th><th className="px-4 py-3">Contact</th></tr>
                            </thead>
                            <tbody>
                                {employees.map(e => (
                                    <tr key={e.id} className="border-b border-slate-100" data-testid={`emp-row-${e.id}`}>
                                        <td className="px-4 py-3 text-sm font-medium">{e.name}</td>
                                        <td className="px-4 py-3 text-sm">{e.designation}</td>
                                        <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{e.department}</td>
                                        <td className="px-4 py-3 text-sm font-mono">₹{Number(e.salary).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{e.phone || e.email || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="attendance" className="mt-4">
                    <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                                <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
                            </thead>
                            <tbody>
                                {employees.map(e => (
                                    <tr key={e.id} className="border-b border-slate-100">
                                        <td className="px-4 py-3 text-sm">{e.name}</td>
                                        <td className="px-4 py-3">
                                            <span className={`status-badge ${attMap[e.id] === "present" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : attMap[e.id] === "absent" ? "bg-red-100 text-red-800 border-red-200" : attMap[e.id] === "leave" ? "bg-yellow-100 text-yellow-800 border-yellow-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                                                {attMap[e.id] || "not marked"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex gap-1 justify-end">
                                                <Button size="sm" variant="outline" onClick={() => markAtt(e.id, "present")} className="h-7 text-xs" data-testid={`att-present-${e.id}`}>P</Button>
                                                <Button size="sm" variant="outline" onClick={() => markAtt(e.id, "absent")} className="h-7 text-xs">A</Button>
                                                <Button size="sm" variant="outline" onClick={() => markAtt(e.id, "leave")} className="h-7 text-xs">L</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="leaves" className="mt-4">
                    <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                                <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">From</th><th className="px-4 py-3">To</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th></tr>
                            </thead>
                            <tbody>
                                {leaves.map(l => (
                                    <tr key={l.id} className="border-b border-slate-100">
                                        <td className="px-4 py-3 text-sm">{employees.find(e => e.id === l.employee_id)?.name || "—"}</td>
                                        <td className="px-4 py-3 text-xs uppercase tracking-wider">{l.leave_type}</td>
                                        <td className="px-4 py-3 text-xs">{l.from_date}</td>
                                        <td className="px-4 py-3 text-xs">{l.to_date}</td>
                                        <td className="px-4 py-3 text-sm">{l.reason}</td>
                                        <td className="px-4 py-3 text-xs uppercase tracking-wider">{l.status}</td>
                                    </tr>
                                ))}
                                {leaves.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-sm text-slate-500">No leave requests</td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}
